import { Box } from '@mui/material';

import type { PePreventionMetric, PeReclassification } from '../types';
import { pct } from '../utils/drilldownFormat';
import { CountLink } from './CountLink';
import { StatCard } from './StatCard';

const MEDIAN_SUBLINE = 'median, students reclassified this year';
const REVIEW_SUBLINE = 'met the review bar, not the exit bar';
const NEEDS_SCORES_SUBLINE = 'needs WIDA ACCESS results';
const NOT_IN_REPORT_SUBLINE = 'not in this report; regenerate to count it';

function formatMedianValue(years: number): string {
  return String(Math.round(years * 10) / 10);
}

export interface ReclassificationCardsProps {
  reclassification: PeReclassification;
  preventionMetric: PePreventionMetric;
  inProgramCount: number;
  ltelMinYears: number;
}

export function ReclassificationCards({
  reclassification,
  preventionMetric,
  inProgramCount,
  ltelMinYears,
}: ReclassificationCardsProps) {
  const met = reclassification.this_year.eligible_count;
  const median = reclassification.median_years_in_program_for_reclassifiers;
  const review = reclassification.review_eligible_count;
  const atRisk = preventionMetric.at_risk_count;
  const band = `in year ${ltelMinYears - 2} or ${ltelMinYears - 1}, before long-term`;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)',
        },
        gap: 2,
      }}
    >
      <StatCard
        title="Met exit criteria"
        value={met === null ? '—' : <CountLink count={met} listKey="met" />}
        unit={met === null ? undefined : 'students'}
        subline={met === null ? NEEDS_SCORES_SUBLINE : `${pct(met, inProgramCount)} of active ELs`}
      />
      <StatCard
        title="Time in program"
        value={median != null ? formatMedianValue(median) : '—'}
        unit={median != null ? 'years' : undefined}
        subline={MEDIAN_SUBLINE}
      />
      <StatCard
        title="Eligible for review"
        value={review == null ? '—' : <CountLink count={review} listKey="review" />}
        unit={review == null ? undefined : 'students'}
        subline={review === null ? NEEDS_SCORES_SUBLINE : review === undefined ? NOT_IN_REPORT_SUBLINE : REVIEW_SUBLINE}
      />
      <StatCard
        title="At risk of LTEL"
        value={atRisk === null ? '—' : <CountLink count={atRisk} listKey="atrisk" />}
        unit={atRisk === null ? undefined : 'students'}
        subline={atRisk === null ? NEEDS_SCORES_SUBLINE : band}
      />
    </Box>
  );
}
